import { Copy, Check, Loader2, Plus } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from "@/com/ui/dialog";
import { Sheet, SheetContent } from "@/com/ui/sheet";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/com/ui/select";

interface CurrencyOption {
	value: string;
	label: string;
}

interface DepositModalComponentProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	isMobile: boolean;
	selectedCurrencyId: string;
	onCurrencyChange: (value: string) => void;
	currencyOptions: CurrencyOption[];
	isLoadingCurrencies?: boolean;
	address: string;
	copied: boolean;
	onCopy: () => void;
	needsGeneration?: boolean;
	isGenerating?: boolean;
	onGenerate?: () => void;
	currencySymbol?: string;
	networkName?: string;
	shortfall?: number;
}

export function DepositModalComponent({
	open,
	onOpenChange,
	isMobile,
	selectedCurrencyId,
	onCurrencyChange,
	currencyOptions,
	isLoadingCurrencies,
	address,
	copied,
	onCopy,
	needsGeneration,
	isGenerating,
	onGenerate,
	currencySymbol,
	networkName,
	shortfall,
}: DepositModalComponentProps) {
	const description =
		shortfall && shortfall > 0
			? `You need $${shortfall.toFixed(2)} more to issue this card. Deposit funds below.`
			: "Select currency to get your deposit address.";

	/* ── Shared body ── */
	const body = (
		<div className="flex flex-col gap-4">
			{/* Currency select */}
			<div className="flex flex-col gap-1.5">
				<label className="text-xs text-muted-foreground uppercase tracking-wider">
					Currency
				</label>
				<Select
					value={selectedCurrencyId}
					onValueChange={onCurrencyChange}
					disabled={isLoadingCurrencies}
				>
					<SelectTrigger className="w-full bg-secondary h-12">
						<SelectValue
							placeholder={
								isLoadingCurrencies
									? "Loading currencies..."
									: "Select currency"
							}
						/>
					</SelectTrigger>
					<SelectContent className="bg-background">
						{currencyOptions.map((c) => (
							<SelectItem key={c.value} value={c.value}>
								{c.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			{/* Generate address button */}
			{needsGeneration && (
				<button
					onClick={onGenerate}
					disabled={isGenerating}
					className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[var(--cta)] text-[var(--cta-foreground)] text-sm font-semibold hover:brightness-95 active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
				>
					{isGenerating ? (
						<Loader2 className="w-4 h-4 animate-spin" />
					) : (
						<Plus className="w-4 h-4" />
					)}
					{isGenerating ? "Generating..." : "Generate Address"}
				</button>
			)}

			{/* Address + QR code block */}
			{address && (
				<div className="flex flex-col items-center gap-4 pt-2">
					{/* QR Code */}
					<div className="rounded-xl bg-white p-1">
						<QRCodeSVG value={address} size={220} level="M" marginSize={3} />
					</div>

					{/* Address — the whole row is tappable to copy */}
					<button
						onClick={onCopy}
						className="w-full rounded-xl bg-secondary p-3 text-left active:scale-[0.99] transition-transform"
					>
						<p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-1.5">
							Deposit Address
						</p>
						<div className="flex items-center gap-2">
							<p className="text-sm font-mono break-all flex-1">{address}</p>
							<span className="shrink-0 w-9 h-9 rounded-lg bg-foreground/10 flex items-center justify-center">
								{copied ? (
									<Check className="w-4 h-4 text-[var(--success)]" />
								) : (
									<Copy className="w-4 h-4 text-muted-foreground" />
								)}
							</span>
						</div>
					</button>

					{currencySymbol && networkName && (
						<p className="text-xs text-muted-foreground text-center">
							Only send{" "}
							<span className="font-semibold text-foreground">
								{currencySymbol}
							</span>{" "}
							via{" "}
							<span className="font-semibold text-foreground">
								{networkName}
							</span>{" "}
							to this address.
						</p>
					)}
				</div>
			)}
		</div>
	);

	/* ── Mobile -> bottom Sheet (full-width, comfortable) ── */
	if (isMobile) {
		return (
			<Sheet open={open} onOpenChange={onOpenChange}>
				<SheetContent
					side="bottom"
					showCloseButton={false}
					className="rounded-t-2xl border-t-0 px-5 pb-8 pt-0 max-h-[92dvh] overflow-y-auto"
				>
					{/* Drag indicator */}
					<div className="flex justify-center py-3">
						<div className="w-10 h-1 rounded-full bg-foreground/20" />
					</div>

					<div className="mb-4">
						<h2 className="text-xl font-bold">Deposit</h2>
						<p className="text-sm text-muted-foreground mt-0.5">
							{description}
						</p>
					</div>

					{body}
				</SheetContent>
			</Sheet>
		);
	}

	/* ── Desktop -> centered Dialog ── */
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="bg-background border-border sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Deposit</DialogTitle>
					<DialogDescription>{description}</DialogDescription>
				</DialogHeader>
				{body}
			</DialogContent>
		</Dialog>
	);
}
